import SPHERELarge from "$root/assets/images/SPHERE_logo_green.svg";
import { UserCircle } from "$root/functional-areas/acl/UserCircle";
import { useUserValue } from "$root/functional-areas/user";
import { Column, type IconName, Row, Text } from "@mdotm/shadcn/legacy/components";
import { HierarchicalLinksModule } from "./HierarchicalLinksModule";
import { type AppDrawerModuleItem, useDevtoolsEnabled, devtoolsPanelOpen$ } from "./AppDrawer-shared";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { logout } from "$root/functional-areas/user/auth";
import { userRepository } from "$root/functional-areas/user/repository";
import { ReactQueryWrapperBase } from "../ReactQueryWrapper";
import { generateImageObjectUrlFromFileResponse } from "../EvolvedPrint/configuration/utils";
import { useTypedNavigation } from "../PlatformRouter/RoutesDef";
import { useAtom } from "@mdotm/shadcn/legacy/reactivity";
import type { CustomerDetailsDTO } from "@eu.mdotm.api/sphere";
import { hasFundsLicense } from "$root/functional-areas/instruments/Instrument-customisation/preset-availability";
import { spawnLipperTermsDialog } from "./LipperTermsDialog";

export function UserModule() {
	const user = useUserValue();
	const customerQuery = userRepository.customer.useQuery(user.customerId!);

	return (
		<Column flexGrow={1} flexBasis={0} gap={12} className="px-6 overflow-auto">
			<Row flexGrow={0} gap={8} alignItems="center" className="sticky top-0 bg-white py-2">
				<UserCircle
					className="shrink-0"
					palette="dark"
					firstName={user.name ?? ""}
					lastName={user.surname ?? ""}
					variant="name"
					size="medium"
				/>
				<Column minSize="0">
					<Text type="Body/M/Bold" className="truncate">{`${user.name ?? ""} ${user.surname ?? ""}`}</Text>
					<Text type="Body/S/Book" className="truncate">
						{user.email ?? ""}
					</Text>
				</Column>
			</Row>
			<HierarchicalLinksModule items={useUserItems(customerQuery.data)} />
			<div className="mt-auto sticky bottom-0 bg-white py-4">
				<ReactQueryWrapperBase query={customerQuery}>
					{({ primaryLogo }) => (
						<img
							src={(primaryLogo && generateImageObjectUrlFromFileResponse(primaryLogo)) ?? SPHERELarge}
							alt="logo"
							className="object-scale-down object-left overflow-hidden w-full h-auto"
							style={{
								backgroundColor: primaryLogo ? themeCSSVars.palette_N0 : themeCSSVars.palette_P500,
							}}
						/>
					)}
				</ReactQueryWrapperBase>
			</div>
		</Column>
	);
}

function useUserItems(customer: CustomerDetailsDTO | undefined): AppDrawerModuleItem[] {
	const { push } = useTypedNavigation();
	const devtoolsEnabled = useDevtoolsEnabled();
	const [devtoolsOpen, setDevtoolsOpen] = useAtom(devtoolsPanelOpen$);
	return [
		{
			enabled: devtoolsEnabled,
			icon: "Terminal" satisfies IconName,
			name: "React Query Devtools",
			title: "React Query Devtools",
			active: devtoolsOpen,
			onClick() {
				setDevtoolsOpen((open) => !open);
			},
		},
		{
			enabled: true,
			icon: "cookies" satisfies IconName,
			name: "Cookie Policy",
			title: "Cookie Policy",
			active: false,
			onClick() {
				window._iub.cs.api.openPreferences();
			},
		},
		{
			enabled: true,
			icon: "Property-1Privacy" satisfies IconName,
			name: "Privacy",
			title: "Privacy",
			active: false,
			onClick() {
				window.open("https://www.iubenda.com/privacy-policy/48016693", "_blank", "noopener noreferrer");
			},
		},
		{
			// The Lipper funds-data license obliges us to show Refinitiv's terms to the users it covers.
			enabled: customer !== undefined && hasFundsLicense(customer),
			icon: "term-and-condition" satisfies IconName,
			name: "Terms & Conditions",
			title: "Terms & Conditions",
			active: false,
			onClick() {
				spawnLipperTermsDialog();
			},
		},
		{
			enabled: true,
			icon: "Exit" satisfies IconName,
			name: "Logout",
			title: "Logout",
			active: false,
			async onClickAsync() {
				// A deliberate sign-out with a live session: its trailing preference writes get their last
				// chance to leave before the token goes (see `settlePendingWrites`).
				await logout({ credentialLost: false });
				push("Login", {});
			},
		},
	];
}
